import React, { useState, useEffect } from 'react'
import { X, Upload, Image, Trash2, Plus } from 'lucide-react'
import { categoryAPI, photoAPI } from '../../services/api'
import CategoryCreateDialog from './CategoryCreateDialog'

const ClipartUploadDialog = ({ isOpen, onClose, onUploadComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [rootCategories, setRootCategories] = useState([])
  const [childCategories, setChildCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSubCategory, setSelectedSubCategory] = useState('')
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSubCategories, setLoadingSubCategories] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [error, setError] = useState('')
  const [showCategoryCreateDialog, setShowCategoryCreateDialog] = useState(false)

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'dev-token-' + Date.now()
  }

  useEffect(() => {
    if (isOpen) {
      fetchCategories()
    }
  }, [isOpen])

  // Fetch subcategories when category is selected
  useEffect(() => {
    if (selectedCategory) {
      fetchSubCategories(selectedCategory)
      setSelectedSubCategory('') // Reset subcategory selection
    } else {
      setChildCategories([])
      setSelectedSubCategory('')
    }
  }, [selectedCategory])

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      // Fetch only root categories (parentId = null)
      const response = await categoryAPI.getAll({ 
        type: 'clipart', 
        parentId: '', // Empty string means parentId is null (root categories)
        isActive: true 
      })

      if (response.data.success) {
        const rootCategoriesData = response.data.data || []
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setRootCategories(rootCategoriesData)
      } else {
        setError('Failed to load categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setError('Failed to load categories')
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchSubCategories = async (parentId) => {
    try {
      if (!parentId) {
        setChildCategories([])
        return
      }
      
      setLoadingSubCategories(true)
      const response = await categoryAPI.getAll({ 
        type: 'clipart', 
        parentId,
        isActive: true 
      })

      if (response.data.success) {
        const childCategoriesData = response.data.data || []
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setChildCategories(childCategoriesData)
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      setChildCategories([])
    } finally {
      setLoadingSubCategories(false)
    }
  }

  const handleCategoryCreated = () => {
    fetchCategories()
    setShowCategoryCreateDialog(false)
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    const imageFiles = files.filter(file => 
      file.type.startsWith('image/') || file.type === 'image/svg+xml'
    )

    if (imageFiles.length !== files.length) {
      setError('Only image files (including SVG) are allowed')
    } else {
      setError('')
    }

    setSelectedFiles(prev => [...prev, ...imageFiles])
    e.target.value = '' // Reset input
  }

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image')
      return
    }

    if (!selectedCategory) {
      setError('Please select a category')
      return
    }

    setUploading(true)
    setError('')
    const progress = {}

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const formData = new FormData()
        formData.append('photo', file) // Use 'photo' field name for photos endpoint
        formData.append('name', file.name.split('.')[0])
        formData.append('category', selectedCategory)
        formData.append('tags', 'clipart') // Tag as clipart for identification
        if (selectedSubCategory) {
          formData.append('subcategory', selectedSubCategory)
        }

        progress[index] = 0
        setUploadProgress({ ...progress })

        // Use photos API for upload (same table as photos)
        try {
          const res = await photoAPI.upload(formData)

          // Ensure 100% on completion
          progress[index] = 100
          setUploadProgress({ ...progress })

          return res.data
        } catch (err) {
          // Detect duplicate error from server
          const status = err?.response?.status
          const code = err?.response?.data?.message || err?.response?.data?.error || ''
          if (status === 409 || code === 'DUPLICATE_IMAGE') {
            progress[index] = 0
            setUploadProgress({ ...progress })
            throw new Error('Duplicate clipart detected')
          }
          throw err
        }
      })

      await Promise.all(uploadPromises)

      // Reset form
      setSelectedFiles([])
      setSelectedCategory('')
      setSelectedSubCategory('')
      setUploadProgress({})

      // Notify parent component
      if (onUploadComplete) {
        onUploadComplete()
      }

      onClose()
    } catch (error) {
      console.error('Error uploading clipart:', error)
      setError(error.message || 'Failed to upload clipart. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">Upload Clipart</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              disabled={uploading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* File Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Clipart Images
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*,.svg"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="clipart-upload-input"
                />
                <label
                  htmlFor="clipart-upload-input"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Choose clipart images to upload
                  </p>
                  <p className="text-sm text-gray-500">
                    Select multiple images at once. Supports JPG, PNG, GIF, WebP, SVG
                  </p>
                </label>
              </div>
            </div>

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Selected Files ({selectedFiles.length})
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Image className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {uploadProgress[index] !== undefined && (
                          <div className="w-16 text-xs text-gray-500">
                            {uploadProgress[index]}%
                          </div>
                        )}
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-gray-200 rounded"
                          disabled={uploading}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loadingCategories || uploading}
                >
                  <option value="">Select a main category</option>
                  {rootCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {loadingCategories && (
                  <p className="text-sm text-gray-500 mt-1">Loading categories...</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subcategory
                </label>
                <select
                  value={selectedSubCategory}
                  onChange={(e) => setSelectedSubCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={!selectedCategory || loadingSubCategories || uploading}
                >
                  <option value="">
                    {!selectedCategory 
                      ? "Select a main category first" 
                      : childCategories.length === 0 && !loadingSubCategories
                      ? "No subcategories available"
                      : "Select a subcategory (optional)"
                    }
                  </option>
                  {childCategories.map((subCategory) => (
                    <option key={subCategory.id} value={subCategory.id}>
                      {subCategory.name}
                    </option>
                  ))}
                </select>
                {loadingSubCategories && (
                  <p className="text-sm text-gray-500 mt-1">Loading subcategories...</p>
                )}
                {selectedCategory && !loadingSubCategories && childCategories.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">No subcategories available for this category</p>
                )}
              </div>
            </div>

            {/* Add Category Button */}
            <div className="mb-6">
              <button
                onClick={() => setShowCategoryCreateDialog(true)}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 text-sm"
                disabled={uploading}
              >
                <Plus className="w-4 h-4" />
                <span>Create New Category</span>
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading || selectedFiles.length === 0 || !selectedCategory}
            >
              {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Clipart${selectedFiles.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* Category Create Dialog */}
      <CategoryCreateDialog
        isOpen={showCategoryCreateDialog}
        onClose={() => setShowCategoryCreateDialog(false)}
        onCategoryCreated={handleCategoryCreated}
        type="clipart"
        availableParentCategories={rootCategories}
        selectedParentCategory={selectedCategory}
      />
    </>
  )
}

export default ClipartUploadDialog
